import React from 'react';
import {
    Box, Container, Heading, Text, Button, VStack, HStack, Flex, Icon, Image, Badge, useToast,
} from '@chakra-ui/react';
import { FiMessageSquare, FiCopy, FiCheckCircle } from 'react-icons/fi';
import { QRCodeCanvas } from 'qrcode.react';
import { smsHref, smsQrPayload, smsBody, textCode } from './textOrderLinks';

const LATER_STEPS = [
    'Left something out? We text back and ask',
    'Reply YES to confirm. We text you at every step',
];

const EXAMPLE_DETAILS = '123 Main St tomorrow';

export default function SiteTextToOrder({ config }) {
    const toast = useToast();
    const canvasWrap = React.useRef(null);
    const [qrImage, setQrImage] = React.useState('');
    const info = config?.textOrdering;
    const themeColor = config?.siteContent?.themeColor || 'blue';

    React.useEffect(() => {
        // A real <img> lets iPhone users long-press the code to open Messages.
        const canvas = canvasWrap.current?.querySelector('canvas');
        try {
            const url = canvas?.toDataURL('image/png');
            if (url && url.startsWith('data:image/png')) setQrImage(url);
        } catch {
            setQrImage('');
        }
    }, [info?.keyword, info?.smsNumber, info?.ownNumber]);

    if (!info?.keyword || !info?.smsNumber) return null;

    const code = textCode(info);
    const href = smsHref(info.smsNumber, code);
    const message = smsBody(code).trim();
    const steps = [
        code ? 'Text the shop code, PICKUP, your address and a day' : 'Text PICKUP, your address and a day',
        ...LATER_STEPS,
    ];

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(message);
            toast({ title: `Copied "${message}"`, description: `Paste it in a text to ${info.smsNumberDisplay}`, status: 'success', duration: 3000 });
        } catch {
            toast({ title: `Text "${message}" to ${info.smsNumberDisplay}`, status: 'info', duration: 5000 });
        }
    };

    return (
        <Box id="text-to-order" py={{ base: 12, md: 16 }} bg={`${themeColor}.50`}>
            <Container maxW="5xl">
                <Flex direction={{ base: 'column', md: 'row' }} align="center" gap={{ base: 8, md: 12 }}
                    bg="white" borderRadius="2xl" boxShadow="lg" p={{ base: 6, md: 10 }}>
                    <VStack align={{ base: 'center', md: 'flex-start' }} spacing={4} flex="1" textAlign={{ base: 'center', md: 'left' }}>
                        <Badge colorScheme={themeColor} borderRadius="full" px={3} py={1}>New</Badge>
                        <Heading size="lg" color="gray.800">Book a pickup by text</Heading>
                        <Text color="gray.600" fontSize={{ base: 'md', md: 'lg' }}>
                            No app, no account. Text{' '}
                            <Text as="span" fontWeight="800" fontFamily="mono" color="gray.800">{message}</Text>{' '}
                            to{' '}
                            <Text as="span" fontWeight="800" color="gray.800" whiteSpace="nowrap">{info.smsNumberDisplay}</Text>
                        </Text>
                        <Box bg="gray.50" border="1px dashed" borderColor="gray.300" borderRadius="lg" px={4} py={2}>
                            <Text fontSize="sm" color="gray.600">
                                Example:{' '}
                                <Text as="span" fontFamily="mono" fontWeight="700" color="gray.800">
                                    {message} {EXAMPLE_DETAILS}
                                </Text>
                            </Text>
                        </Box>

                        <VStack align="flex-start" spacing={2} pt={1}>
                            {steps.map((step) => (
                                <HStack key={step} spacing={2} align="flex-start">
                                    <Icon as={FiCheckCircle} color={`${themeColor}.500`} mt={1} />
                                    <Text color="gray.700" fontSize="sm" textAlign="left">{step}</Text>
                                </HStack>
                            ))}
                        </VStack>

                        <HStack spacing={3} pt={2} flexWrap="wrap" justify={{ base: 'center', md: 'flex-start' }}>
                            <Button as="a" href={href} size="lg" colorScheme={themeColor} borderRadius="full"
                                leftIcon={<FiMessageSquare />} display={{ base: 'inline-flex', md: 'none' }}>
                                Text us now
                            </Button>
                            <Button onClick={copy} size="lg" variant="outline" borderRadius="full" leftIcon={<FiCopy />}>
                                Copy message
                            </Button>
                        </HStack>

                        <Text fontSize="xs" color="gray.500">
                            Msg &amp; data rates may apply. Reply HELP for help, STOP to opt out.
                        </Text>
                    </VStack>

                    <VStack spacing={3} flexShrink={0}>
                        <Box ref={canvasWrap} display="none" aria-hidden="true">
                            <QRCodeCanvas value={smsQrPayload(info.smsNumber, code)} size={440} level="M" includeMargin />
                        </Box>
                        {qrImage ? (
                            <Box as="a" href={href} borderRadius="xl" border="1px solid" borderColor="gray.200" p={2} bg="white">
                                <Image src={qrImage} alt={`Scan to text ${message} to ${info.smsNumberDisplay}`}
                                    boxSize={{ base: '180px', md: '220px' }} />
                            </Box>
                        ) : null}
                        <Text fontSize="sm" color="gray.600" textAlign="center" maxW="220px">
                            <Text as="span" display={{ base: 'none', md: 'inline' }}>Scan with your phone camera to open a text with everything filled in.</Text>
                            <Text as="span" display={{ base: 'inline', md: 'none' }}>Tap the code to open Messages with everything filled in.</Text>
                        </Text>
                    </VStack>
                </Flex>
            </Container>
        </Box>
    );
}
